import React, { useRef } from "react";
import { useServices } from "../../services/Services";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import css from "./Sidebar.module.css";
import TopicDeepDiveDialog from "../Dialog/TopicDeepDiveDialog";
import { DialogHandle } from "../Dialog/DialogTypes";
import { Add } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { useSubscriptionWithQuery } from "../../hooks/useSubscription";

interface AnalysisData {
  id: string;
  title: string | undefined;
  createdAt: string | undefined;
}

interface InfiniteQueryPage {
  data: AnalysisData[];
  nextPageToken?: string;
}

interface SidebarTopicSectionProps {
  userId: string;
  isCurrentRoute: (path: string) => boolean;
  onItemSelect: () => void;
}

const SidebarTopicSection: React.FC<SidebarTopicSectionProps> = ({
  userId,
  isCurrentRoute,
  onItemSelect,
}) => {
  const services = useServices();
  const topicDialogRef = useRef<DialogHandle>(null);
  const createTopicDeepDiveRequest =
    services.Request.useCreateTopicDeepDiveRequest();

  // Subscribe to topic deep dive requests and read from cached data
  const { data: trackedTopicDeepDiveRequests = [] } = useSubscriptionWithQuery({
    subscribe: () => services.Request.subscribeToTopicDeepDiveRequest(userId),
    query: services.Request.useGetTrackedTopicDeepDiveRequests(userId),
    enabled: !!userId,
    onError: (error) => {
      console.error("Failed to subscribe to topic deep dive requests:", error);
    },
  });

  const {
    data: topicData,
    fetchNextPage: fetchNextTopicPage,
    hasNextPage: hasNextTopicPage,
    isFetchingNextPage: isFetchingNextTopicPage,
  } = services.Analysis.useGetTopicDeepDivesInfinite(userId, 10);

  const topicDeepDives: AnalysisData[] =
    topicData?.pages?.flatMap((page: InfiniteQueryPage) => page.data) ?? [];

  const newTopicRequests = trackedTopicDeepDiveRequests.filter(
    (t) => t.status === "New",
  );
  const pendingTopicRequests = trackedTopicDeepDiveRequests.filter(
    (t) => t.status === "Pending",
  );
  const failedTopicRequests = trackedTopicDeepDiveRequests.filter(
    (t) => t.status === "Failed",
  );

  const handleRetry = async (topic: string) => {
    try {
      await createTopicDeepDiveRequest.mutateAsync({
        topic,
        userId,
      });
    } catch (error) {
      console.error("Failed to retry topic deep dive:", error);
    }
  };

  return (
    <>
      <div className={css.sectionTitle}>
        <span className={css.sectionTitleText}>Topic Deep-Dives</span>
        <div className={css.sectionTitleAction}>
          <button
            className={css.inlineAddButton}
            onClick={() => topicDialogRef.current?.show()}
            title="New Topic Deep Dive"
          >
            <Add className={css.inlineAddIcon} /> New
          </button>
        </div>
      </div>
      <TopicDeepDiveDialog ref={topicDialogRef} />
      <div className={css.insightsList}>
        {/* New, Pending and Failed Requests */}
        {(pendingTopicRequests.length > 0 ||
          failedTopicRequests.length > 0 ||
          newTopicRequests.length > 0) && (
          <>
            {newTopicRequests.map((topicRequest) => {
              return (
                <div
                  key={`${topicRequest.id}-${topicRequest.payload}`}
                  className={`${css.insightItem} ${css.newItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={topicRequest.payload}>
                      {topicRequest.payload}
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingTopicRequests.map((topicRequest) => {
              return (
                <div
                  key={`${topicRequest.id}-${topicRequest.payload}`}
                  className={`${css.insightItem} ${css.pendingItem}`}
                >
                  <div className={css.itemContent}>
                    <div
                      className={css.itemTitle}
                      title={topicRequest?.payload}
                    >
                      {topicRequest?.payload}
                    </div>
                  </div>
                  <div className={css.loadingSpinner}>
                    <CircularProgress size={16} />
                  </div>
                </div>
              );
            })}
            <div className={css.sectionSeparator} />
            {failedTopicRequests.map((topicRequest) => {
              return (
                <div
                  key={`${topicRequest.id}-${topicRequest.payload}`}
                  className={`${css.insightItem} ${css.failedItem}`}
                >
                  <div className={css.itemContent}>
                    <div className={css.itemTitle} title={topicRequest.payload}>
                      {topicRequest?.payload}
                    </div>
                  </div>
                  <button
                    className={css.retryButton}
                    onClick={() => handleRetry(topicRequest.payload || "")}
                    disabled={createTopicDeepDiveRequest.isPending}
                  >
                    Retry
                  </button>
                </div>
              );
            })}
          </>
        )}

        {/* Completed Deep Dives */}
        {topicDeepDives.map((analysis) => {
          const path = `/topic-analysis/${analysis.id}`;
          const title = analysis.title || "Topic Analysis";
          return (
            <Link
              key={analysis.id}
              to={path}
              onClick={onItemSelect}
              className={`${css.insightItem} ${isCurrentRoute(path) ? css.currentRoute : ""}`}
            >
              <div className={css.itemContent}>
                <div className={css.itemTitle} title={title}>
                  {title}
                </div>
              </div>
              <div className={css.itemDate}>
                {analysis.createdAt
                  ? format(new Date(analysis.createdAt), "MMM dd")
                  : "Recent"}
              </div>
            </Link>
          );
        })}
        {hasNextTopicPage && (
          <button
            className={css.loadMoreButton}
            onClick={() => fetchNextTopicPage()}
            disabled={isFetchingNextTopicPage}
          >
            {isFetchingNextTopicPage ? "Loading..." : "Load More"}
          </button>
        )}
      </div>
    </>
  );
};

export default SidebarTopicSection;
